/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {ChakraProvider} from '@chakra-ui/react'

// Removes focus for non-keyboard interactions for the whole application
import 'focus-visible/dist/focus-visible'

import Fonts from '../../fonts'
import {UserInteractionProvider} from '../../hooks/use-user-interaction'
import {MyRegistryAPIProvider} from '../../myregistry-api'
import theme from '../../theme'
import CommerceAPI from '../../commerce-api'
import {
    BasketProvider,
    CommerceAPIProvider,
    CustomerProductListsProvider,
    SiteProvider,
    CustomerProvider
} from '../../commerce-api/contexts'
import GoogleMapsAPI from '../../googlemaps-api'
import {GoogleMapsAPIProvider} from '../../googlemaps-api/contexts'
import GoogleTranslateAPI from '../../google-translate-api'
import {GoogleTranslateAPIProvider} from '../../google-translate-api/contexts'
import CuralateAPI from '../../curalate-api'
import {CuralateAPIProvider} from '../../curalate-api/contexts'
import {MultiSiteProvider} from '../../contexts'
import {resolveSiteFromUrl} from '../../utils/site-utils'
import {resolveLocaleFromUrl} from '../../utils/utils'
import {getConfig} from 'pwa-kit-runtime/utils/ssr-config'
import {GtmConfigProvider} from '../../gtm-api/contexts'
import {createUrlTemplate} from '../../utils/url'
import BazaarvoiceAPI from '../../bazaarvoice-api'
import {BazaarvoiceAPIProvider} from '../../bazaarvoice-api/context'
import {CroProvider} from '../cro'
import {compileTemplate} from '../../utils/text-utils'
import {localeToBVLocale} from '../../bazaarvoice-api/utils'
import {InteractionStudioProvider} from '../../interaction-studio-api'

/**
 * Use the AppConfig component to inject extra arguments into the getProps
 * methods for all Route Components in the app – typically you'd want to do this
 * to inject a connector instance that can be used in all Pages.
 *
 * You can also use the AppConfig to configure a state-management library such
 * as Redux, or Mobx, if you like.
 */
const AppConfig = ({children, locals = {}}) => {
    const [basket, setBasket] = useState(null)
    const [customer, setCustomer] = useState(null)
    const ocapiSite = useRef(locals.ocapiSite)
    const ocapiSitePreferences = locals.ocapiSite?.custom?.c_result?.preferences || {}

    return (
        <MultiSiteProvider site={locals.site} locale={locals.locale} buildUrl={locals.buildUrl}>
            <CommerceAPIProvider value={locals.api}>
                <SiteProvider value={ocapiSite}>
                    <CustomerProvider value={{customer, setCustomer}}>
                        <BasketProvider value={{basket, setBasket}}>
                            <GoogleTranslateAPIProvider value={locals.googletranslateapi}>
                                <GoogleMapsAPIProvider value={locals.googlemapsapi}>
                                    <CuralateAPIProvider value={locals.curalateapi}>
                                        <BazaarvoiceAPIProvider value={locals.bazaarvoiceapi}>
                                            <GtmConfigProvider value={locals.gtmConfig}>
                                                <MyRegistryAPIProvider
                                                    enabled={ocapiSitePreferences.myRegistry}
                                                    siteKey={ocapiSitePreferences.myRegistrySiteKey}
                                                >
                                                    <InteractionStudioProvider
                                                        enabled={
                                                            ocapiSitePreferences.isInteractionStudioEnabled
                                                        }
                                                        scriptUrl={
                                                            ocapiSitePreferences.interactionStudioJS
                                                        }
                                                        overridesEnabled={
                                                            ocapiSitePreferences.isInteractionStudioOverridesEnabled
                                                        }
                                                    >
                                                        <CroProvider>
                                                            <CustomerProductListsProvider>
                                                                <Fonts />
                                                                <ChakraProvider theme={theme}>
                                                                    <UserInteractionProvider>
                                                                        {children}
                                                                    </UserInteractionProvider>
                                                                </ChakraProvider>
                                                            </CustomerProductListsProvider>
                                                        </CroProvider>
                                                    </InteractionStudioProvider>
                                                </MyRegistryAPIProvider>
                                            </GtmConfigProvider>
                                        </BazaarvoiceAPIProvider>
                                    </CuralateAPIProvider>
                                </GoogleMapsAPIProvider>
                            </GoogleTranslateAPIProvider>
                        </BasketProvider>
                    </CustomerProvider>
                </SiteProvider>
            </CommerceAPIProvider>
        </MultiSiteProvider>
    )
}

AppConfig.restore = (locals = {}, frozen = {}) => {
    const path =
        typeof window === 'undefined'
            ? locals.originalUrl
            : `${window.location.pathname}${window.location.search}`
    const site = resolveSiteFromUrl(path)
    const locale = resolveLocaleFromUrl(path)
    const currency = locale.preferredCurrency

    const {app: appConfig} = getConfig()
    const apiConfig = {
        ...appConfig.commerceAPI,
        einsteinConfig: appConfig.einsteinAPI,
        bazaarvoiceConfig: appConfig.bazaarvoiceAPI,
        storefrontConfig: appConfig.storefrontAPI,
        curalateAPI: appConfig.curalateAPI
    }

    apiConfig.parameters.siteId = site.id
    apiConfig.einsteinConfig.siteId = `${appConfig.einsteinAPI.siteId.split('-')[0]}-${site.id}`
    apiConfig.storefrontConfig.parameters.siteId = site.id
    if (frozen.ocapiSite) {
        const {
            bvApiEnv = 'stg',
            bvApiKey,
            bvApiScriptUrlTemplate,
            curalateDataSourceId,
            googleMapsApiKey,
            googleTranslateEnabled,
            googleTranslateApiKey
        } = frozen.ocapiSite.custom.c_result.preferences
        appConfig.curalateAPI.dataSourceId = curalateDataSourceId
        appConfig.bazaarvoiceAPI = {
            ...appConfig.bazaarvoiceAPI,
            ...appConfig.bazaarvoiceAPI.proxies[bvApiEnv],
            bvApiKey,
            bvApiEnv,
            bvApiScriptUrl: compileTemplate(bvApiScriptUrlTemplate, {
                locale: localeToBVLocale(locale)
            })
        }
        appConfig.googleMapsAPI.apiKey = googleMapsApiKey
        appConfig.googleTranslateAPI.apiKey = googleTranslateApiKey
        appConfig.googleTranslateAPI.apiEnabled = googleTranslateEnabled
    }

    locals.curalateapi = new CuralateAPI({...appConfig.curalateAPI, locale})
    locals.bazaarvoiceapi = new BazaarvoiceAPI({...appConfig.bazaarvoiceAPI, locale})
    locals.googlemapsapi = new GoogleMapsAPI({...appConfig.googleMapsAPI, locale})
    locals.googletranslateapi = new GoogleTranslateAPI({...appConfig.googleTranslateAPI, locale})
    locals.api = new CommerceAPI({...apiConfig, locale: locale.id, currency})
    locals.gtmConfig = appConfig.gtmAPI
    locals.buildUrl = createUrlTemplate(appConfig, site.alias || site.id, locale.id)
    locals.site = site
    locals.locale = locale
    locals.ocapiSite = frozen.ocapiSite
}

AppConfig.freeze = (locals) => {
    return {
        ocapiSite: locals?.ocapiSite
    }
}

AppConfig.extraGetPropsArgs = (locals = {}) => {
    return {
        api: locals.api,
        buildUrl: locals.buildUrl,
        site: locals.site,
        locale: locals.locale,
        ocapiSite: locals.ocapiSite
    }
}

AppConfig.propTypes = {
    children: PropTypes.node,
    locals: PropTypes.object
}

export default AppConfig
